import React, { useEffect, useState } from 'react';
import { Form, Button, Card, InputNumber, Row, Col, Switch, Modal } from 'antd';
import { FormattedMessage, useIntl } from 'react-intl';
import { ArrowForward } from '@material-ui/icons';
import toast from 'components/Basic/Toast';
import Loading from 'components/Basic/Loading';
import { SensitiveInput, SecretRefInput } from 'components/Basic/SensitiveInput';
import { useLazyGetIntegrationQuery, useUpdateIntegrationMutation } from 'core/integrations/IntegrationsService';

const SLUG = 'sar';

interface SarSettings {
  apiUrl: string;
  defaultAppId: string;
  getProductAppId: string;
  cia: string;
  version: string;
  clientCode: number | null;
  uuid: string;
  productDbService: string;
  orderService: string;
  supportCashOnDelivery: boolean;
  autoOrderDelivery: boolean;
  trackOrder: boolean;
  cyberMonday: boolean;
  noCheckFreeAmbassador: boolean;
  dedicatedRestaurant: string;
  maximumSingleOrderPurchase: number | null;
  maximumGroupOrderPurchase: number | null;
  cacheTtlMinutes: number | null;
}

const DEFAULT_SETTINGS: SarSettings = {
  apiUrl: '',
  defaultAppId: '',
  getProductAppId: '',
  cia: '',
  version: '',
  clientCode: null,
  uuid: '',
  productDbService: '',
  orderService: '',
  supportCashOnDelivery: false,
  autoOrderDelivery: false,
  trackOrder: false,
  cyberMonday: false,
  noCheckFreeAmbassador: false,
  dedicatedRestaurant: '',
  maximumSingleOrderPurchase: null,
  maximumGroupOrderPurchase: null,
  cacheTtlMinutes: null,
};

const isSecretRef = (v: any): boolean => v !== null && typeof v === 'object' && '$secretRef' in v;

const toStr = (v: any): string => (isSecretRef(v) ? '' : v ?? '');
const toBool = (v: any): boolean => (isSecretRef(v) ? false : Boolean(v));
const toNum = (v: any): number | null => (isSecretRef(v) ? null : v ?? null);

const FIELD_DESCRIPTIONS: Record<string, string> = {
  apiUrl: 'SAR endpoint URL.',
  defaultAppId: 'App ID for order requests.',
  cia: 'Company identifier sent in every SAR request.',
  version: 'Protocol version required by SAR.',
  secretKey: 'Signs each request using HMAC-SHA256.',
  clientCode: 'Numeric client code sent with each order.',
  uuid: 'Device identifier sent with order payloads.',
  productDbService: 'SAR webservice name for catalog queries.',
  orderService: 'SAR webservice name for order submissions.',
  maximumSingleOrderPurchase: 'Maximum purchase amount per single order.',
  maximumGroupOrderPurchase: 'Maximum purchase amount per group order.',
};

const SarConfig: React.FC = () => {
  const intl = useIntl();

  const [sarInfo, setSarInfo] = useState<SarSettings>(DEFAULT_SETTINGS);
  const [initialState, setInitialState] = useState<SarSettings>(DEFAULT_SETTINGS);
  const [secretRefs, setSecretRefs] = useState<Record<string, string>>({});
  const [overriddenFields, setOverriddenFields] = useState<Set<string>>(new Set());
  const [secretKeyRef, setSecretKeyRef] = useState('SAR_SECRET_KEY');
  const [resetKey, setResetKey] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const [getIntegration] = useLazyGetIntegrationQuery();
  const [updateIntegration] = useUpdateIntegrationMutation();

  useEffect(() => {
    setIsLoading(true);
    getIntegration({ slug: SLUG })
      .unwrap()
      .then(res => {
        const s = res.settings ?? {};

        const refs: Record<string, string> = {};
        for (const [key, val] of Object.entries(s)) {
          if (isSecretRef(val)) refs[key] = (val as any).$secretRef;
        }
        setSecretRefs(refs);

        if (s.secretKey?.$secretRef) setSecretKeyRef(s.secretKey.$secretRef);

        const loaded: SarSettings = {
          apiUrl: toStr(s.apiUrl),
          defaultAppId: toStr(s.defaultAppId),
          getProductAppId: toStr(s.getProductAppId),
          cia: toStr(s.cia),
          version: toStr(s.version),
          clientCode: toNum(s.clientCode),
          uuid: toStr(s.uuid),
          productDbService: toStr(s.productDbService),
          orderService: toStr(s.orderService),
          supportCashOnDelivery: toBool(s.supportCashOnDelivery),
          autoOrderDelivery: toBool(s.autoOrderDelivery),
          trackOrder: toBool(s.trackOrder),
          cyberMonday: toBool(s.cyberMonday),
          noCheckFreeAmbassador: toBool(s.noCheckFreeAmbassador),
          dedicatedRestaurant: toStr(s.dedicatedRestaurant),
          maximumSingleOrderPurchase: toNum(s.maximumSingleOrderPurchase),
          maximumGroupOrderPurchase: toNum(s.maximumGroupOrderPurchase),
          cacheTtlMinutes: toNum(s.cacheTtlMinutes),
        };
        setInitialState(loaded);
        setSarInfo(loaded);
        setOverriddenFields(new Set());
      })
      .catch(() => {})
      .finally(() => setIsLoading(false));
  }, [getIntegration]);

  const handleChange = <K extends keyof SarSettings>(field: K, value: SarSettings[K]) => {
    setSarInfo(prev => ({ ...prev, [field]: value }));
    setOverriddenFields(prev => new Set(Array.from(prev).concat(field as string)));
  };

  const onCancel = () => {
    setSarInfo(initialState);
    setOverriddenFields(new Set());
    setResetKey(k => k + 1);
  };

  const buildSettingsPayload = (): Record<string, any> => {
    const payload: Record<string, any> = {};

    const restoreOrSet = (field: string, value: any, emptyValue: any) => {
      if (secretRefs[field] && !overriddenFields.has(field)) {
        payload[field] = { $secretRef: secretRefs[field] };
      } else if (value !== emptyValue) {
        payload[field] = value;
      } else {
        payload[field] = emptyValue === '' ? null : emptyValue;
      }
    };

    restoreOrSet('apiUrl', sarInfo.apiUrl, '');
    restoreOrSet('defaultAppId', sarInfo.defaultAppId, '');
    restoreOrSet('getProductAppId', sarInfo.getProductAppId, '');
    restoreOrSet('cia', sarInfo.cia, '');
    restoreOrSet('version', sarInfo.version, '');
    restoreOrSet('uuid', sarInfo.uuid, '');
    restoreOrSet('productDbService', sarInfo.productDbService, '');
    restoreOrSet('orderService', sarInfo.orderService, '');
    restoreOrSet('dedicatedRestaurant', sarInfo.dedicatedRestaurant, '');
    restoreOrSet('maximumSingleOrderPurchase', sarInfo.maximumSingleOrderPurchase, null);
    restoreOrSet('maximumGroupOrderPurchase', sarInfo.maximumGroupOrderPurchase, null);
    restoreOrSet('cacheTtlMinutes', sarInfo.cacheTtlMinutes, null);
    restoreOrSet('clientCode', sarInfo.clientCode, null);
    restoreOrSet('supportCashOnDelivery', sarInfo.supportCashOnDelivery, false);
    restoreOrSet('autoOrderDelivery', sarInfo.autoOrderDelivery, false);
    restoreOrSet('trackOrder', sarInfo.trackOrder, false);
    restoreOrSet('cyberMonday', sarInfo.cyberMonday, false);
    restoreOrSet('noCheckFreeAmbassador', sarInfo.noCheckFreeAmbassador, false);

    return payload;
  };

  const doSave = async () => {
    if (!sarInfo.apiUrl && !secretRefs['apiUrl']) {
      toast.error({ title: intl.formatMessage({ id: 'The field must not be empty!' }) });
      return;
    }
    setIsLoading(true);
    try {
      await updateIntegration({
        slug: SLUG,
        body: { settings: buildSettingsPayload() },
      }).unwrap();
      setInitialState(sarInfo);
      setOverriddenFields(new Set());
      setResetKey(k => k + 1);
      toast.success({ title: intl.formatMessage({ id: 'SAR configuration updated successfully!' }) });
    } catch {
      toast.error({ title: intl.formatMessage({ id: 'Error updating SAR configuration' }) });
    } finally {
      setIsLoading(false);
    }
  };

  const onSave = () => {
    Modal.confirm({
      title: intl.formatMessage({ id: 'Save changes' }),
      content: intl.formatMessage({ id: 'Save integration warning' }),
      okText: intl.formatMessage({ id: 'Save' }),
      cancelText: intl.formatMessage({ id: 'Cancel' }),
      className: 'integration_confirm_modal',
      onOk: doSave,
    });
  };

  const isRef = (field: string) => !!secretRefs[field] && !overriddenFields.has(field);

  const refInput = (field: string, regular: React.ReactNode): React.ReactNode =>
    isRef(field) ? <SecretRefInput envVarName={secretRefs[field]} /> : regular;

  const refExtra = (field: string, defaultExtra?: React.ReactNode): React.ReactNode | undefined => {
    if (!isRef(field)) return defaultExtra;
    const descNode = defaultExtra ?? (FIELD_DESCRIPTIONS[field] ? <FormattedMessage id={FIELD_DESCRIPTIONS[field]} /> : null);
    return descNode ? (
      <>{descNode}{' '}<FormattedMessage id="Value is managed as a server environment variable." /></>
    ) : (
      <FormattedMessage id="Value is managed as a server environment variable." />
    );
  };

  const switchOrRef = (
    field: 'supportCashOnDelivery' | 'autoOrderDelivery' | 'trackOrder' | 'cyberMonday' | 'noCheckFreeAmbassador',
    labelId: string,
    descId: string,
    inputId: string,
    lastItem = false,
  ) =>
    isRef(field) ? (
      <Form.Item
        label={
          <span className="title" style={{ marginBottom: 0 }}>
            <FormattedMessage id={labelId} />
          </span>
        }
        extra={<><FormattedMessage id={descId} />{' '}<FormattedMessage id="Value is managed as a server environment variable." /></>}
        style={{ marginBottom: lastItem ? 0 : 14 }}
      >
        <SecretRefInput envVarName={secretRefs[field]} />
      </Form.Item>
    ) : (
      <div className="section_toggle_row" style={lastItem ? { marginBottom: 0 } : undefined}>
        <div className="section_toggle_label">
          <p className="title">
            <FormattedMessage id={labelId} />
          </p>
          <p className="description">
            <FormattedMessage id={descId} />
          </p>
        </div>
        <Switch id={inputId} checked={sarInfo[field]} onChange={(v: boolean) => handleChange(field, v)} />
      </div>
    );

  return (
    <div className="integrations_layout">
      <Loading visible={isLoading} />

      <div className="integrations_grid">
        <div className="integrations_left_col">
          <Card title={<FormattedMessage id="Connection" />} size="small" style={{ borderRadius: 8 }}>
            <Form layout="vertical" colon={false}>
              <Form.Item
                label={
                  <span className="title" style={{ marginBottom: 0 }}>
                    <FormattedMessage id="Api URL" />
                  </span>
                }
                extra={refExtra('apiUrl')}
                style={{ marginBottom: 14 }}
              >
                {refInput(
                  'apiUrl',
                  <SensitiveInput
                    id="integration-sar-api-url"
                    key={`apiUrl-${resetKey}`}
                    value={sarInfo.apiUrl}
                    placeholder="https://"
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange('apiUrl', e.target.value)}
                  />,
                )}
              </Form.Item>
              <Form.Item
                label={
                  <span className="title" style={{ marginBottom: 0 }}>
                    <FormattedMessage id="App ID" />
                  </span>
                }
                extra={refExtra('defaultAppId')}
                style={{ marginBottom: 14 }}
              >
                {refInput(
                  'defaultAppId',
                  <SensitiveInput
                    id="integration-sar-default-app-id"
                    key={`defaultAppId-${resetKey}`}
                    value={sarInfo.defaultAppId}
                    placeholder="Enter App ID"
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange('defaultAppId', e.target.value)}
                  />,
                )}
              </Form.Item>
              <Form.Item
                label={
                  <span className="title" style={{ marginBottom: 0 }}>
                    <FormattedMessage id="Get Product App ID" />
                  </span>
                }
                extra={refExtra('getProductAppId', <FormattedMessage id="App ID for catalog requests. Leave empty to use App ID." />)}
                style={{ marginBottom: 14 }}
              >
                {refInput(
                  'getProductAppId',
                  <SensitiveInput
                    id="integration-sar-get-product-app-id"
                    key={`getProductAppId-${resetKey}`}
                    value={sarInfo.getProductAppId}
                    placeholder="Enter Get Product App ID"
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange('getProductAppId', e.target.value)}
                  />,
                )}
              </Form.Item>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    label={
                      <span className="title" style={{ marginBottom: 0 }}>
                        <FormattedMessage id="CIA Code" />
                      </span>
                    }
                    extra={refExtra('cia')}
                    style={{ marginBottom: 0 }}
                  >
                    {refInput(
                      'cia',
                      <SensitiveInput
                        id="integration-sar-cia"
                        key={`cia-${resetKey}`}
                        value={sarInfo.cia}
                        placeholder="e.g. 001"
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange('cia', e.target.value)}
                      />,
                    )}
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label={
                      <span className="title" style={{ marginBottom: 0 }}>
                        <FormattedMessage id="SAR Version" />
                      </span>
                    }
                    extra={refExtra('version')}
                    style={{ marginBottom: 0 }}
                  >
                    {refInput(
                      'version',
                      <SensitiveInput
                        id="integration-sar-version"
                        key={`version-${resetKey}`}
                        value={sarInfo.version}
                        placeholder="e.g. 1.0"
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange('version', e.target.value)}
                      />,
                    )}
                  </Form.Item>
                </Col>
              </Row>
            </Form>
          </Card>

          <Card title={<FormattedMessage id="SAR Client" />} size="small" style={{ borderRadius: 8 }}>
            <Form layout="vertical" colon={false}>
              <Form.Item
                label={
                  <span className="title" style={{ marginBottom: 0 }}>
                    <FormattedMessage id="Secret Key" />
                  </span>
                }
                extra={<><FormattedMessage id="Signs each request using HMAC-SHA256." />{' '}<FormattedMessage id="Value is managed as a server environment variable." /></>}
                style={{ marginBottom: 14 }}
              >
                <SecretRefInput envVarName={secretKeyRef} />
              </Form.Item>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    label={
                      <span className="title" style={{ marginBottom: 0 }}>
                        <FormattedMessage id="Client Code" />
                      </span>
                    }
                    extra={refExtra('clientCode')}
                    style={{ marginBottom: 14 }}
                  >
                    {refInput(
                      'clientCode',
                      <InputNumber
                        id="integration-sar-client-code"
                        value={sarInfo.clientCode}
                        placeholder="Enter client code"
                        onChange={(v: number | null) => handleChange('clientCode', v)}
                        style={{ width: '100%' }}
                      />,
                    )}
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label={
                      <span className="title" style={{ marginBottom: 0 }}>
                        <FormattedMessage id="UUID" />
                      </span>
                    }
                    extra={refExtra('uuid')}
                    style={{ marginBottom: 14 }}
                  >
                    {refInput(
                      'uuid',
                      <SensitiveInput
                        id="integration-sar-uuid"
                        key={`uuid-${resetKey}`}
                        value={sarInfo.uuid}
                        placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange('uuid', e.target.value)}
                      />,
                    )}
                  </Form.Item>
                </Col>
              </Row>
              <Form.Item
                label={
                  <span className="title" style={{ marginBottom: 0 }}>
                    <FormattedMessage id="Product DB Service" />
                  </span>
                }
                extra={refExtra('productDbService')}
                style={{ marginBottom: 14 }}
              >
                {refInput(
                  'productDbService',
                  <SensitiveInput
                    id="integration-sar-product-db-service"
                    key={`productDbService-${resetKey}`}
                    value={sarInfo.productDbService}
                    placeholder="Enter product DB service name"
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      handleChange('productDbService', e.target.value)
                    }
                  />,
                )}
              </Form.Item>
              <Form.Item
                label={
                  <span className="title" style={{ marginBottom: 0 }}>
                    <FormattedMessage id="Order Service" />
                  </span>
                }
                extra={refExtra('orderService')}
                style={{ marginBottom: 14 }}
              >
                {refInput(
                  'orderService',
                  <SensitiveInput
                    id="integration-sar-order-service"
                    key={`orderService-${resetKey}`}
                    value={sarInfo.orderService}
                    placeholder="Enter order service name"
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange('orderService', e.target.value)}
                  />,
                )}
              </Form.Item>
              <Form.Item
                label={
                  <span className="title" style={{ marginBottom: 0 }}>
                    <FormattedMessage id="Dedicated Restaurant" />
                  </span>
                }
                extra={refExtra(
                  'dedicatedRestaurant',
                  <FormattedMessage id="Routes all orders to this restaurant. Testing only." />,
                )}
                style={{ marginBottom: 0 }}
              >
                {refInput(
                  'dedicatedRestaurant',
                  <SensitiveInput
                    id="integration-sar-dedicated-restaurant"
                    key={`dedicatedRestaurant-${resetKey}`}
                    value={sarInfo.dedicatedRestaurant}
                    placeholder="Leave empty for production"
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      handleChange('dedicatedRestaurant', e.target.value)
                    }
                  />,
                )}
              </Form.Item>
            </Form>
          </Card>
        </div>

        <div className="integrations_right_col">
          <Card title={<FormattedMessage id="Order Behavior" />} size="small" style={{ borderRadius: 8 }}>
            <Form layout="vertical" colon={false}>
              {switchOrRef(
                'supportCashOnDelivery',
                'Cash on Delivery',
                'Accepts cash payment for delivery orders.',
                'integration-sar-support-cash-on-delivery',
              )}
              {switchOrRef(
                'autoOrderDelivery',
                'Auto Order Delivery',
                'Sends delivery orders to SAR automatically.',
                'integration-sar-auto-order-delivery',
              )}
              {switchOrRef(
                'trackOrder',
                'Track Order',
                'Receives tracking updates from SAR.',
                'integration-sar-track-order',
              )}
              {switchOrRef(
                'cyberMonday',
                'Cyber Monday',
                'Activates Cyber Monday mode.',
                'integration-sar-cyber-monday',
              )}
              {switchOrRef(
                'noCheckFreeAmbassador',
                'Skip Free Ambassador Check',
                'Skips the free ambassador availability check.',
                'integration-sar-no-check-free-ambassador',
              )}
              <Form.Item
                label={
                  <span className="title" style={{ marginBottom: 0 }}>
                    <FormattedMessage id="Max Single Order Purchase" />
                  </span>
                }
                extra={refExtra('maximumSingleOrderPurchase')}
                style={{ marginTop: 8, marginBottom: 14 }}
              >
                {refInput(
                  'maximumSingleOrderPurchase',
                  <InputNumber
                    id="integration-sar-max-single-order-purchase"
                    value={sarInfo.maximumSingleOrderPurchase}
                    placeholder="e.g. 50000"
                    onChange={(v: number | null) => handleChange('maximumSingleOrderPurchase', v)}
                    style={{ width: '100%' }}
                  />,
                )}
              </Form.Item>
              <Form.Item
                label={
                  <span className="title" style={{ marginBottom: 0 }}>
                    <FormattedMessage id="Max Group Order Purchase" />
                  </span>
                }
                extra={refExtra('maximumGroupOrderPurchase')}
                style={{ marginBottom: 14 }}
              >
                {refInput(
                  'maximumGroupOrderPurchase',
                  <InputNumber
                    id="integration-sar-max-group-order-purchase"
                    value={sarInfo.maximumGroupOrderPurchase}
                    placeholder="e.g. 30000"
                    onChange={(v: number | null) => handleChange('maximumGroupOrderPurchase', v)}
                    style={{ width: '100%' }}
                  />,
                )}
              </Form.Item>
              <Form.Item
                label={
                  <span className="title" style={{ marginBottom: 0 }}>
                    <FormattedMessage id="Cache TTL (minutes)" />
                  </span>
                }
                extra={<FormattedMessage id="How long the server caches SAR settings before re-reading from the database. Leave empty to use the default (720 min)." />}
                style={{ marginBottom: 0 }}
              >
                <InputNumber
                  id="integration-sar-cache-ttl-minutes"
                  value={sarInfo.cacheTtlMinutes}
                  placeholder="720"
                  min={1}
                  onChange={(v: number | null) => handleChange('cacheTtlMinutes', v)}
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Form>
          </Card>
        </div>
      </div>

      <div className="integrations_actions">
        <Button id="integration-sar-save" onClick={onSave}>
          <FormattedMessage id="Save" />
          <ArrowForward />
        </Button>
        <p id="integration-sar-cancel" className="integrations_cancel_btn" onClick={onCancel}>
          <FormattedMessage id="Cancel" />
        </p>
      </div>
    </div>
  );
};

export default SarConfig;
